import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
} from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import {
  editPatientButton,
  registerPatientButtonStyles,
  DeletePatientButton,
  addButtonPadding,
} from "../../../common/commonStyles";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import validators from "../../../utils/validations/validators";
import api from "../../../services/api";

export default function SlotassignDialog({
  open,
  onClose,
  handleSlotSubmit,
  serviceDetails,
  therapyPlansData,
  handlePatientSlotDelete,
  handlePatientSlotEdit,
  branchId,
}) {
  const transformedData = therapyPlansData.map((patient) => ({
    ...patient,
    services: Object.values(patient.services),
  }));

  const [formData, setFormData] = useState({});
  const [isEdit, setEdit] = useState(false);
  const [recordID, setRecordID] = useState();
  const [validationErrors, setValidationErrors] = useState({});
  const [slots, setSlots] = useState([]);

  const handleTimeslotChange = useCallback(
    async (id) => {
      let timeSlots = await api.get(
        `app/slots/?branch_id=${branchId}&service_id=${id}`
      );
      if (timeSlots?.status === 200) {
        setSlots(timeSlots?.data);
      }
    },
    [branchId]
  );

  useEffect(() => {
    if (branchId && formData?.service_id) {
      handleTimeslotChange(formData?.service_id);
    }
  }, [formData?.service_id, branchId, handleTimeslotChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let parsedValue = value;

    // Only split value into array if it is from a multi-select
    if (Array.isArray(value)) {
      parsedValue = value; // already an array (Select with `multiple`)
    } else if (name === "therapy_days") {
      parsedValue = value.split(","); // fallback if manually added comma-separated value
    } else if (!isNaN(value)) {
      parsedValue = Number(value); // convert numeric strings to numbers
    }

    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
      is_slot_allocated: true, // if you want this flag
    }));

    // Validate live
    if (validators[name]) {
      const errorMsg = validators[name](value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };
  const requiredFields = [
    "service_id",
    "total_sessions",
    "frequency_per_week",
    "time_slot",
    "therapy_days",
  ];

  const isFormValid =
    Object.values(validationErrors).every((err) => err === "") &&
    requiredFields.every((field) => {
      const val = formData[field];
      if (Array.isArray(val)) {
        return val.length > 0; // for therapy_days
      }
      return val !== "" && val !== null && val !== undefined;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formattedData = {
      ...formData,
      therapy_days: Array.isArray(formData.therapy_days)
        ? formData.therapy_days.join(", ")
        : formData.therapy_days,
      slot_id: slots.find((slot) => slot.label === formData.time_slot)?.slot_id,
    };
    if (isEdit) {
      await handlePatientSlotEdit(formattedData, recordID);
    } else {
      await handleSlotSubmit(formattedData);
    }
    setFormData({});
  };

  const handleslotDelete = async (id) => {
    handlePatientSlotDelete(id);
  };

  const handleslotEdit = async (id, serviceName) => {
    setEdit(true);
    setRecordID(id);
    const filtered = transformedData
      .map((patient) => ({
        ...patient,
        services: patient.services.filter(
          (service) => service.record_id === id
        ),
      }))
      .filter((patient) => patient.services.length > 0);

    const transformedfiltered = filtered.flatMap((patient) =>
      patient.services.map((service) => ({
        service_name: service.service_name,
        record_id: service.record_id,
        is_slot_allocated: service.session_id !== null,
        total_sessions: service.total_sessions,
        frequency_per_week: service.therapy_days
          ? service.therapy_days.split(",").length
          : 0,
        time_slot: service.time_slot || "",
        therapy_days: service.therapy_days
          ? service.therapy_days.split(",").map((day) => day.trim())
          : [],
      }))
    );

    const matchedService = serviceDetails.find(
      (service) => service.name === serviceName
    );

    // Rename `id` to `service_id` and flatten
    const { id: service_id, ...restService } = matchedService || {};
    const flattened = {
      ...transformedfiltered[0],
      ...restService,
      service_id, // renamed from id
    };
    setFormData(flattened);
  };

  const allDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const columns = [
    {
      field: "service_name",
      headerName: "Service Name",
      flex: 1, // Takes proportional space
      minWidth: 150, // Minimum width to prevent too much shrinking
      editable: false,
    },
    {
      field: "frequency_per_week",
      headerName: "Frequency Per Week",
      flex: 1,
      minWidth: 150,
      editable: false,
    },
    {
      field: "total_sessions",
      headerName: "Total Session",
      flex: 1,
      minWidth: 150,
      editable: false,
    },
    {
      field: "therapy_days",
      headerName: "Therapy Days",
      flex: 1,
      minWidth: 150,
      editable: false,
    },
    {
      field: "time_slot",
      headerName: "Time Slot",
      flex: 0.8, // Slightly smaller proportion
      minWidth: 110,
      editable: false,
    },
    {
      field: "",
      headerName: "Action",
      width: 150, // Keep fixed width for action buttons
      editable: false,
      sortable: false,
      renderCell: (params) => (
        <Box className="center-align">
          <DeleteIcon
            sx={DeletePatientButton}
            onClick={() => {
              handleslotDelete(params.row.record_id);
            }}
          />
          <BorderColorIcon
            sx={editPatientButton}
            onClick={() => {
              handleslotEdit(params.row.record_id, params.row.service_name);
            }}
          />
        </Box>
      ),
    },
  ];
  return (
    <Dialog
      open={open}
      onClose={() => {
        setFormData({});
        setValidationErrors({});
        setEdit(false);
        setRecordID(null);
        onClose();
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle className="">Assign Therapy To Patient</DialogTitle>
      <DialogContent>
        <Box className="service_assign_patientform">
          <Grid
            container
            spacing={4}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Grid size={2}>
              <FormControl fullWidth variant="standard">
                <InputLabel className="custom-selectfield-label">
                  Service Name
                </InputLabel>
                <Select
                  value={formData?.service_id || []}
                  name="service_id"
                  label="Service Name"
                  onChange={(e) => {
                    handleChange(e);
                    const selectedId = e.target.value;
                    handleTimeslotChange(selectedId);
                  }}
                  className="custom-selectfield"
                >
                  {serviceDetails?.map((step) => (
                    <MenuItem value={step.id}>{step?.name}</MenuItem>
                  ))}
                </Select>
                {validationErrors.service_id && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      margin: "3px 0 0",
                    }}
                  >
                    {validationErrors.service_id}
                  </p>
                )}
              </FormControl>
            </Grid>
            <Grid size={2}>
              <TextField
                name="total_sessions"
                label="Total Sessions"
                value={formData?.total_sessions || ""}
                onChange={handleChange}
                fullWidth
                margin="dense"
                variant="standard"
                className="custom-textfield"
                error={!!validationErrors.total_sessions}
                helperText={validationErrors.total_sessions}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                name="frequency_per_week"
                label="Frequency Per Week"
                value={formData?.frequency_per_week || ""}
                onChange={handleChange}
                fullWidth
                margin="dense"
                variant="standard"
                className="custom-textfield"
                error={!!validationErrors.frequency_per_week}
                helperText={validationErrors.frequency_per_week}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                select
                className="custom-textfield"
                name="time_slot"
                label="Time Slot"
                value={formData?.time_slot || ""}
                onChange={handleChange}
                fullWidth
                margin="dense"
                variant="standard"
                error={!!validationErrors.time_slot}
                helperText={validationErrors.time_slot}
              >
                {slots.map((slot) => (
                  <MenuItem key={slot.slot_id} value={slot.label}>
                    {slot.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={2}>
              <FormControl sx={{ width: 200 }} variant="standard">
                <InputLabel>Therapy Days</InputLabel>
                <Select
                  fullWidth
                  multiple
                  name="therapy_days"
                  value={formData?.therapy_days || []}
                  onChange={handleChange}
                  renderValue={(selected) => selected.join(", ")}
                  className="custom-select"
                >
                  {allDays.map((name) => (
                    <MenuItem key={name} value={name}>
                      <Checkbox
                        checked={formData?.therapy_days?.includes(name)}
                      />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.therapy_days && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      margin: "3px 0 0",
                    }}
                  >
                    {validationErrors.therapy_days}
                  </p>
                )}
              </FormControl>
            </Grid>
            <Grid size={2} sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                sx={[registerPatientButtonStyles, addButtonPadding]}
                size="small"
                onClick={handleSubmit}
                disabled={!isFormValid}
              >
                {isEdit ? "EDIT" : "Create"}
              </Button>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            sx={{
              fontFamily: "Nunito, sans-serif",
              // Cell (row content) font size
              "& .MuiDataGrid-cell": {
                fontSize: "12px",
              },

              // Header font size
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "13px",
                fontWeight: "bold",
              },
            }}
            rows={transformedData[0]?.services}
            getRowId={(row) => row.record_id}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          onClick={() => {
            setFormData({});
            setValidationErrors({});
            setEdit(false);
            setRecordID(null);
            onClose();
          }}
          type="button"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
